// ByContent.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Image, message, Input, Row, Col, Segmented, Divider, InputNumber, FloatButton, Pagination } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { formatDate, scrollPageToTop } from './Util.js';
import { BASE_URL } from './Config.js';
import ContentCard from './ContentCard';
import useIsMobile from './useIsMobile';
import './ByContent.css';

import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn'

dayjs.locale('zh-cn');

const ByContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [contentId, setContentId] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [areaId, setAreaId] = useState('');
  const [segmentValue, setSegmentValue] = useState('内容');
  const [getPageMaxNum, setGetPageMaxNum] = useState(0);
  const [lastTime, setLastTime] = useState(0);
  const [lastCount, setLastCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    setAreaId("1");
  }, []);

  // 新增：监听 segmentValue 变化，切换到"爬取数据"时调用 getQueryInfo
  useEffect(() => {
    if (segmentValue === '爬取数据') {
      getQueryInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentValue]);

  const fetchData = async (page = 1, size = 10, append = false) => {
    setLoading(true);
    try {
      // 发送请求获取数据
      let url = `${BASE_URL}/xhg/getKeepContent?page_no=${page}&page_size=${size}`;

      if (contentId.trim() !== '') {
        url += `&id=${contentId.trim()}`;
      }

      const response = await axios.get(url);

      console.log(response);

      if (!response.data.data.lf_content) {
        if (!append) {
          setData([]);
          setTotalItems(0);
        }
        return;
      }

      const formattedData = response.data.data.lf_content.map(item => ({
        ...item,
        create_time_text: formatDate(item.create_time),
        update_time_text: formatDate(item.update_time),
        update_time: item.update_time,
      }));
      console.log(response.data.data.lf_num);
      setData(prev => (append ? [...prev, ...formattedData] : formattedData));
      setCurrentPage(page);
      setPageSize(size);
      setTotalItems(response.data.data.lf_num);
    } catch (error) {
      message.error('获取数据失败: ' + error, 5);
    } finally {
      setLoading(false);
    }
  };

  const handleQuery = () => {
    // 移动端信息流：查询始终从第一页开始
    if (isMobile) {
      fetchData(1, pageSize, false);
    } else {
      fetchData(currentPage);
    }
  };

  // 翻页：直接加载对应页码的内容，并把外层滚动条带回顶部
  const handlePageChange = (page, size) => {
    fetchData(page, size || pageSize, false);
    scrollPageToTop();
  };

  const goDetail = (record) => {
    if (!record) return;
    navigate(`/detail/by/${record.id}`, {
      // 带上当前列表页位置，详情页作为浮层打开，返回时列表状态不丢
      state: { record, type: 'by', backgroundLocation: location },
    });
  };

  const resetQueryData = () => {
    setContentId('');
    setAreaId('1');
  };

  const onChangePageMaxNum = (e) => {
    console.log(e);
    setGetPageMaxNum(e);
  };

  const getByData = async () => {
    try {
      // 发送请求获取数据
      let page_no = Number(getPageMaxNum);
      let url = `${BASE_URL}/xhg/crawlKeepContent?page_no=${page_no}`;

      const response = await axios.get(url);

      console.log(response);
    } catch (error) {
      message.error('获取数据失败: ' + error, 5);
    }
  };

  const columns = [
    // 定义表格列
    { title: 'tid', dataIndex: 'tid', key: 'tid', width: 50, fixed: 'left' },
    { title: '标题', dataIndex: 'title', key: 'title', width: 100 },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      width: 200,
      render: (text) => {
        // 防御性处理：确保 text 是字符串，并处理可能的字面量 \n
        const safeText = typeof text === 'string' ? text.replace(/\\n/g, '\n') : text;
        return (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: '1.5' }}>
            {safeText}
          </div>
        );
      },
    },
    {
      title: '图片',
      dataIndex: 'img_urls',
      key: 'img_urls',
      width: 250,
      render: (imageUrls) => {
        // 1. 基础判断：确保有数据且不全为空字符串
        const hasImages =
          imageUrls && Array.isArray(imageUrls) && imageUrls.some(url => url && url.trim() !== '');

        if (!hasImages) {
          return null;
        }

        return (
          // 2. 创建 Flex 容器
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '4px 0' }}>
            {imageUrls.map((url, index) => {
              // 3. 过滤掉空链接
              if (!url || url.trim() === '') return null;

              return (
                <Image
                  key={`image_${index}`}
                  src={url.trim()}
                  alt={`图片${index + 1}`}
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '4px',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              );
            })}
          </div>
        );
      },
    },
    { title: '城市', dataIndex: 'location', key: 'location', width: 100 },
    { title: '联系方式', dataIndex: 'contact', key: 'contact', width: 150, fixed: 'right' },
  ];

  const getQueryInfo = async () => {
    try {
      // 发送请求获取数据
      let url = `${BASE_URL}/xhg/getKeepQueryInfo`;

      const response = await axios.get(url);

      console.log(response);

      // 修改：数据在 response.data.data 下（与 fetchData 保持一致）
      const { last_time, count } = response.data.data;

      // 修改：last_time 是毫秒时间戳，需要格式化
      const formattedTime = formatDate(last_time);

      setLastTime(formattedTime);
      setLastCount(count);
    } catch (error) {
      message.error('获取数据失败: ' + error, 5);
    }
  };

  const handleCancel = () => setPreviewOpen(false);

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  return (
    <div>
      <div>
        <Segmented size="large" options={['内容', '爬取数据']} value={segmentValue} onChange={setSegmentValue} />
      </div>

      {segmentValue === '内容' && (
        <div>
          <Divider />
          <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 12]}>
            <Col className='gutter-row' xs={24} sm={8}>
              <Input
                addonBefore="内容id"
                placeholder="内容id"
                value={contentId}
                onChange={(e) => setContentId(e.target.value)}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>

          <Row
            gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 12]}
            style={{ marginTop: 12 }}
          >
            <Col className='gutter-row' xs={24} sm={{ span: 8, offset: 16 }}>
              <Button type="primary" onClick={handleQuery} loading={loading}>查询</Button>
              <Button type="default" onClick={() => resetQueryData()} style={{ marginLeft: '10px' }}>重置</Button>
            </Col>
          </Row>

          {isMobile ? (
            <div className="feed-list">
              {data.length === 0 && !loading && (
                <div className="feed-empty">点击「查询」查看信息流</div>
              )}
              {data.map((item, index) => (
                <ContentCard key={item.id || index} record={item} type="by" onOpen={goDetail} />
              ))}
              {totalItems > 0 && (
                <div className="feed-pagination">
                  <Pagination
                    size="small"
                    current={currentPage}
                    total={totalItems}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    showTotal={(total) => `共 ${total} 条`}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="table-scroll-box">
              <Table
                dataSource={data}
                columns={columns}
                pagination={{
                  onChange: handlePageChange,
                  current: currentPage,
                  defaultPageSize: 10,
                  pageSizeOptions: ['10', '20', '30', '50'],
                  showSizeChanger: true,
                  total: totalItems,
                }}
                scroll={{ x: 1500, y: 1200 }}
                rowKey="id"
                rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-light' : 'table-row-dark')}
              />
            </div>
          )}
        </div>
      )}

      {segmentValue === '爬取数据' && (
        <div>
          <Divider />
          <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 12]}>
            {/* 删除了 Cascader */}
            <Col className='gutter-row' xs={24} sm={8}>
              <InputNumber
                min={0}
                max={10}
                defaultValue={0}
                onChange={onChangePageMaxNum}
                addonBefore="爬取页数"
                style={{ width: '100%' }}
              />
            </Col>
          </Row>

          <Row
            gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 12]}
            style={{ marginTop: 12 }}
          >
            <Col className='gutter-row' xs={24} sm={{ span: 8, offset: 16 }}>
              <Button type="primary" onClick={() => getByData()} style={{ marginBottom: '10px' }}>爬取数据</Button>
              <Button type="default" onClick={() => resetQueryData()} style={{ marginLeft: '10px', marginBottom: '10px' }}>重置</Button>
            </Col>
          </Row>

          <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 12]}>
            <Col className='gutter-row' xs={24} sm={12}>
              <Input
                addonBefore="上次爬取时间"
                value={lastTime}
                disabled
                style={{ marginBottom: '10px' }}
              />
            </Col>
            <Col className='gutter-row' xs={24} sm={12}>
              <Input
                addonBefore="上次爬取数量"
                value={lastCount}
                disabled
                style={{ marginBottom: '10px' }}
              />
            </Col>
          </Row>
        </div>
      )}

      <FloatButton.BackTop
        target={isMobile ? undefined : () => document.querySelector('.table-scroll-box .ant-table-body')}
        visibilityHeight={100}
        duration={500}
      />
    </div>
  );
};

export default ByContent;
